import { addXAuthTokenHeaders } from "@dues/apis/customAddHeaders";
import axios, { type Method } from "axios";

interface acInterceptorProps {
	baseURL: string;
	headersBeforeRequest?: Record<string, string>;
	addXAuthToken?: boolean;
}

interface RequestOptions {
	headers?: Record<string, string>;
	transformData?: (data: any) => any;
	localCache?: unknown;
}

/**
 * Creates an axios-backed request runner mirroring the previous alova interceptor.
 * Each verb executes the request when called and resolves with the response data.
 * @property {string} baseURL Base URL for the requests.
 * @property {Object} [headersBeforeRequest] Custom headers included in requests.
 * @property {boolean} [addXAuthToken = true] Whether the X-Auth-Token header is added. Default true.
 */
const acInterceptor = ({
	baseURL,
	headersBeforeRequest,
	addXAuthToken = true,
}: acInterceptorProps) => {
	const request = async <T>(
		method: Method,
		path: string,
		body: unknown,
		options: RequestOptions = {},
	): Promise<T> => {
		const isGeminio = baseURL?.includes("geminio");

		let headers: Record<string, string> = { ...options.headers };

		if (!isGeminio) {
			headers = { ...headers, ...headersBeforeRequest };

			if (addXAuthToken) {
				const shim = { url: path, config: { headers } };
				await addXAuthTokenHeaders(shim);
				headers = shim.config.headers;
			}
		}

		try {
			const response = await axios.request({
				method,
				url: `${baseURL}${path}`,
				data: body,
				headers,
				validateStatus: () => true,
			});

			if (response.status >= 400) {
				throw {
					...response.data,
					status: response.status,
					statusText: response.statusText,
					url: response.config?.url,
				};
			}

			const data = response.data;
			return (options.transformData ? options.transformData(data) : data) as T;
		} catch (error) {
			window?.DD_RUM?.onReady(() => {
				window?.DD_RUM?.addError(error);
			});
			throw error;
		}
	};

	return {
		Get: <T = unknown>(path: string, options: RequestOptions = {}) =>
			request<T>("GET", path, undefined, options),
		Post: <T = unknown>(
			path: string,
			body?: unknown,
			options: RequestOptions = {},
		) => request<T>("POST", path, body, options),
		Put: <T = unknown>(
			path: string,
			body?: unknown,
			options: RequestOptions = {},
		) => request<T>("PUT", path, body, options),
		Patch: <T = unknown>(
			path: string,
			body?: unknown,
			options: RequestOptions = {},
		) => request<T>("PATCH", path, body, options),
		Delete: <T = unknown>(
			path: string,
			body?: unknown,
			options: RequestOptions = {},
		) => request<T>("DELETE", path, body, options),
	};
};

export default acInterceptor;
